// frontend/src/lib/calendarHeight.js
//
// The Calendar's own height, chosen with the drag handle on its bottom edge
// (feedback #844, Shawn: "the calendar needs to be much bigger … and be
// adjustable down"; James 2026-11-03).  One height for Month, Week and Day:
// the box pages/Calendar.js sizes (the month grid's week rows, the Week /
// Day time grid).
//
//   - Kept in design px (px at zoom 1) and drawn × the automatic zoom
//     (lib/displayZoom.js), like every other px size around rem content
//     (rules/responsive-layout.md), so the same choice shows the same rows
//     at another zoom.
//   - At least 360 px × zoom, at most the window's height.
//   - Remembered on this computer, per person:
//     localStorage['lextrack_calendar_height:<user id>'] (absent = the
//     default size, which follows the window as before).  Registered in
//     lib/uiPrefs.js "Layout + navigation" (the signed-in person's own key
//     only; another person's is never listed or cleared), so Profile →
//     Reset UI Preferences puts the default back.
//   - Double-clicking the handle forgets it (the default size again).
//
// Every storage access is wrapped: private mode, blocked site data or a
// thumbnail capture can throw, and the Calendar must still draw.

export const CALENDAR_HEIGHT_KEY = 'lextrack_calendar_height'
export const CALENDAR_HEIGHT_MIN = 360 // design px (× zoom on screen)
export const CALENDAR_HEIGHT_STEP = 40 // ↑ / ↓ on the handle, design px

const noUser = (uid) => uid === null || uid === undefined || uid === ''
const storageOf = (storage) => storage || (typeof window !== 'undefined' ? window.localStorage : null)

export function calendarHeightKeyFor (uid) {
  return `${CALENDAR_HEIGHT_KEY}:${uid}`
}

// A stored value → a positive whole number of design px, else null.
export function normalizeCalendarHeight (value) {
  if (value === null || value === undefined || value === '') return null
  const n = Math.round(Number(value))
  return Number.isFinite(n) && n > 0 ? n : null
}

// This person's chosen height on this computer (design px), or null for the
// default size (nobody named, nothing stored, unreadable storage).
export function readCalendarHeight (uid, storage) {
  if (noUser(uid)) return null
  try {
    const s = storageOf(storage)
    return s ? normalizeCalendarHeight(s.getItem(calendarHeightKeyFor(uid))) : null
  } catch { return null }
}

// Keep (or, with null, forget) the height.  false = nobody to keep it for,
// or storage refused it (the height still applies for this visit).
export function writeCalendarHeight (uid, designPx, storage) {
  if (noUser(uid)) return false
  const v = normalizeCalendarHeight(designPx)
  try {
    const s = storageOf(storage)
    if (!s) return false
    if (v === null) s.removeItem(calendarHeightKeyFor(uid))
    else s.setItem(calendarHeightKeyFor(uid), String(v))
    return true
  } catch { return false }
}

// The handle's range in screen px: 360 × zoom up to the window's height
// (never below the minimum, even in a window shorter than that).
export function calendarHeightBounds (zoom, windowHeight) {
  const z = Number.isFinite(zoom) && zoom > 0 ? zoom : 1
  const min = Math.round(CALENDAR_HEIGHT_MIN * z)
  const h = Number.isFinite(windowHeight) ? Math.round(windowHeight) : min
  return { min, max: Math.max(min, h) }
}

export function clampCalendarHeight (px, bounds) {
  const n = Number.isFinite(px) ? Math.round(px) : bounds.min
  return Math.min(bounds.max, Math.max(bounds.min, n))
}

// A stored design height → screen px at this zoom and window.
export function calendarHeightOnScreen (designPx, zoom, windowHeight) {
  const z = Number.isFinite(zoom) && zoom > 0 ? zoom : 1
  return clampCalendarHeight(designPx * z, calendarHeightBounds(z, windowHeight))
}

// Screen px → the design px kept in storage.
export function calendarHeightToDesign (screenPx, zoom) {
  const z = Number.isFinite(zoom) && zoom > 0 ? zoom : 1
  return Math.round(screenPx / z)
}
